/* eslint-disable react/jsx-pascal-case */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unused-prop-types */
import { Grid, Box, Stack, Typography, IconButton } from '@mui/material';
import { useHttpClient } from 'hooks';
import * as React from 'react';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { Entity, MappedEntityType } from 'services/resolvers';
import Loadable from 'components/ui/Loadable';
import NoRecordsFound from './NoRecordsFound';
import { RenderParameters } from 'pdfjs-dist/types/src/display/api';
import { IconChevronLeft, IconChevronRight, IconArrowsMaximize } from '@tabler/icons-react';
import FormDialog from 'berry/components/dialogs/FormDialog';
import CV_Icon from '../Icon/CV_Icon';

if ((window as any).pdfjsLib) {
    (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.7.570/pdf.worker.js`;
}

const pdfjsLib = (window as any).pdfjsLib;

const ERROR_TEXT = 'Unable to load document.';

export type PdfViewerProps = {
    fileUrl: string;
    showFullscreen?: boolean;
    justFullscreen?: boolean;
    fullScreenButton?: React.ReactElement;
    disabled?: boolean;
    onClick?: () => void;
    configuredIcon?: React.ReactElement;
    disabledConfiguredIcon?: React.ReactElement;
};

const FullScreenPdfViewer = ({ fileUrl, fullScreenButton, disabled = false, onClick = () => {} }: PdfViewerProps) => (
    <>
        {fullScreenButton && (
            <FormDialog
                type="icon"
                disabled={disabled}
                buttonSx={{
                    backgroundColor: 'transparent',
                    border: 'none'
                }}
                showActions={false}
                buttonText={fullScreenButton}
                title=""
                onOpen={onClick}
            >
                <Box
                    sx={{
                        width: '750px',
                        paddingTop: 2,
                        position: 'relative'
                    }}
                >
                    <PdfViewer fileUrl={fileUrl} showFullscreen={false} />
                </Box>
            </FormDialog>
        )}
    </>
);

const PdfViewer = ({ fileUrl, showFullscreen = true }: PdfViewerProps) => {
    const canvasRef = React.useRef<React.LegacyRef<HTMLCanvasElement>>();

    const [pdfRef, setPdfRef] = React.useState<PDFDocumentProxy>();
    const [currentPage, setCurrentPage] = React.useState(1);

    const renderPage = React.useCallback(
        (pageNum: number, pdf = pdfRef) => {
            pdf &&
                canvasRef &&
                pdf.getPage(pageNum).then((page) => {
                    const viewport = page.getViewport({ scale: 1.5 });
                    const canvas = canvasRef.current as unknown as HTMLCanvasElement;
                    canvas.height = viewport.height;
                    canvas.width = viewport.width;
                    const renderContext = {
                        canvasContext: canvas.getContext('2d'),
                        viewport
                    } as RenderParameters;
                    page.render(renderContext);
                });
        },
        [pdfRef]
    );

    React.useEffect(() => {
        renderPage(currentPage, pdfRef);
    }, [pdfRef, currentPage, renderPage]);

    React.useEffect(() => {
        console.log(fileUrl);
        pdfjsLib.getDocument(fileUrl).promise.then((pdfDocument: any) => {
            setPdfRef(pdfDocument);
        });
    }, [fileUrl]);

    const nextPage = () => pdfRef && currentPage < pdfRef.numPages && setCurrentPage(currentPage + 1);

    const prevPage = () => currentPage >= 1 && setCurrentPage(currentPage - 1);

    return (
        <>
            {showFullscreen && (
                <Grid container justifyContent="flex-end">
                    <Grid item xs={2}>
                        <FullScreenPdfViewer fileUrl={fileUrl} fullScreenButton={<IconArrowsMaximize color="#000" />} />
                    </Grid>
                </Grid>
            )}
            <Grid container justifyContent="center" sx={{ mt: -2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={prevPage} disabled={currentPage <= 1}>
                        <IconChevronLeft color="#000" />
                    </IconButton>

                    <Typography variant="subtitle1" color="#000">
                        {currentPage} / {pdfRef?.numPages}
                    </Typography>

                    <IconButton onClick={nextPage} disabled={pdfRef && currentPage === pdfRef.numPages}>
                        <IconChevronRight color="#000" />
                    </IconButton>
                </Stack>
            </Grid>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    // paddingBottom: 'calc(842 / 595 * 100%)', // Maintain A4 aspect ratio
                    position: 'relative'
                }}
            >
                <canvas style={{ maxWidth: '100%', maxHeight: '100%' }} ref={canvasRef as unknown as React.LegacyRef<HTMLCanvasElement>} />
            </Box>
        </>
    );
};

export const AuthorizePdfViewer = ({
    fileUrl,
    justFullscreen,
    onClick = () => {},
    configuredIcon = <CV_Icon />,
    disabledConfiguredIcon = <CV_Icon mainColor="#CBCBCB" backColor="#6C6C6C" />
}: PdfViewerProps) => {
    const { asyncDetachedRequest: getDocument } = useHttpClient<unknown>(Entity.Generic, MappedEntityType.GetDocument, null);
    const [documentUrlBlob, setDocumentUrlBlob] = React.useState<string | boolean>(true);
    const [loaded, setLoaded] = React.useState(true);

    const loadDocument = async () => {
        try {
            const rawDocument = await getDocument({ path: fileUrl });
            const blob = await (rawDocument as Response).blob();
            const url = URL.createObjectURL(blob);
            setDocumentUrlBlob(url);
            setLoaded(false);
        } catch (error) {
            setDocumentUrlBlob(false);
            setLoaded(false);
        }
    };

    React.useEffect(() => {
        loadDocument();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileUrl]);

    return (
        <>
            {!justFullscreen && (
                <Loadable open={loaded}>
                    {!loaded && documentUrlBlob && <PdfViewer fileUrl={documentUrlBlob as string} />}
                    {!loaded && !documentUrlBlob && <NoRecordsFound text={ERROR_TEXT} />}
                </Loadable>
            )}
            {justFullscreen && (
                <Loadable open={loaded}>
                    {!loaded && documentUrlBlob && (
                        <FullScreenPdfViewer fileUrl={documentUrlBlob as string} fullScreenButton={configuredIcon} onClick={onClick} />
                    )}
                    {!loaded && !documentUrlBlob && <IconButton disabled>{disabledConfiguredIcon}</IconButton>}
                </Loadable>
            )}
        </>
    );
};

export default PdfViewer;
